import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { NGXLogger } from 'ngx-logger';
import * as maplibregl from 'maplibre-gl';
import { SchedeService } from '../../../services/schede.service';
import { Scheda } from '../../../models/scheda.model';
import proj4 from 'proj4';
import { CreazioneSchedaDTO } from '../../../models/requests/scheda-creazione.model';
import { SchedaAggiornaGeometriaDTO } from '../../../models/requests/scheda-aggiorna-geometria.model';

@Component({
  selector: 'app-carica-geojson',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    FileUploadModule,
    TooltipModule
  ],
  templateUrl: './carica-geojson.component.html',
  styleUrl: './carica-geojson.component.scss'
})
export class CaricaGeoJSONComponent implements OnInit {
  @ViewChild('map') mapContainer!: ElementRef;
  @Input() visible: boolean = false;
  @Input() scheda: Scheda | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() geojsonCaricato = new EventEmitter<any>();

  private map: maplibregl.Map | null = null;
  uploadedGeoJSON: File | null = null;
  submitting: boolean = false;

  constructor(
    private logger: NGXLogger,
    private messageService: MessageService,
    private schedeService: SchedeService
  ) {}

  ngOnInit() {
    // Configura proj4 per la conversione di coordinate
    proj4.defs('EPSG:32632', '+proj=utm +zone=32 +datum=WGS84 +units=m +no_defs');
  }

  show(scheda: Scheda) {
    this.scheda = scheda;
    this.visible = true;
    setTimeout(() => {
      this.initializeMap();
    }, 100);
  }

  onGeoJSONSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      this.uploadedGeoJSON = file;
      // Leggiamo il contenuto del file GeoJSON
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const geoJSONData = JSON.parse(e.target.result);
          // Convertiamo le coordinate da EPSG:32632 a EPSG:4326
          const convertedGeoJSON = this.convertCoordinates(geoJSONData);
          // Inizializziamo la mappa e aggiungiamo il layer con il GeoJSON
          setTimeout(() => {
            if (this.mapContainer && this.mapContainer.nativeElement) {
              this.initializeMap(convertedGeoJSON);
            } else {
              this.messageService.add({
                severity: 'error',
                summary: 'Errore mappa',
                detail: 'Container della mappa non disponibile',
                life: 5000
              });
            }
          }, 100);
        } catch (error) {
          console.error('Errore nel parsing del file GeoJSON:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Il file GeoJSON non è valido',
            life: 5000
          });
        }
      };
      reader.readAsText(file);
    }
  }

  private convertCoordinates(geoJSON: any): any {
    // Definiamo le proiezioni
    const fromProj = 'EPSG:32632';
    const toProj = 'EPSG:4326';

    // Funzione per convertire una singola coordinata
    const convertCoordinate = (coord: number[]): number[] => {
      const [x, y] = coord;
      const [lon, lat] = proj4(fromProj, toProj, [x, y]);
      return [lon, lat];
    };

    // Funzione per convertire ricorsivamente le coordinate
    const convertCoordinatesRecursive = (coordinates: any): any => {
      if (Array.isArray(coordinates[0])) {
        return coordinates.map((coord: any) => convertCoordinatesRecursive(coord));
      } else {
        return convertCoordinate(coordinates);
      }
    };

    // Creiamo una copia profonda del GeoJSON
    const convertedGeoJSON = JSON.parse(JSON.stringify(geoJSON));

    // Convertiamo le coordinate per ogni feature
    convertedGeoJSON.features = convertedGeoJSON.features.map((feature: any) => {
      if (feature.geometry) {
        switch (feature.geometry.type) {
          case 'Point':
            feature.geometry.coordinates = convertCoordinate(feature.geometry.coordinates);
            break;
          case 'MultiPoint':
            feature.geometry.coordinates = feature.geometry.coordinates.map(convertCoordinate);
            break;
          case 'LineString':
            feature.geometry.coordinates = feature.geometry.coordinates.map(convertCoordinate);
            break;
          case 'MultiLineString':
            feature.geometry.coordinates = feature.geometry.coordinates.map((lineString: any) =>
              lineString.map(convertCoordinate)
            );
            break;
          case 'Polygon':
            feature.geometry.coordinates = feature.geometry.coordinates.map((ring: any) =>
              ring.map(convertCoordinate)
            );
            break;
          case 'MultiPolygon':
            feature.geometry.coordinates = feature.geometry.coordinates.map((polygon: any) =>
              polygon.map((ring: any) => ring.map(convertCoordinate))
            );
            break;
          case 'GeometryCollection':
            feature.geometry.geometries = feature.geometry.geometries.map((geom: any) => {
              if (geom.coordinates) {
                geom.coordinates = convertCoordinatesRecursive(geom.coordinates);
              }
              return geom;
            });
            break;
        }
      }
      return feature;
    });

    return convertedGeoJSON;
  }

  private initializeMap(geoJSONData?: any): void {
    if (!this.mapContainer || !this.mapContainer.nativeElement) return;

    // Se la mappa è già inizializzata, rimuoviamo il layer esistente
    if (this.map) {
      if ((this.map as any).getLayer('geojson-layer-fill')) {
        (this.map as any).removeLayer('geojson-layer-fill');
      }
      if ((this.map as any).getLayer('geojson-layer-line')) {
        (this.map as any).removeLayer('geojson-layer-line');
      }
      if ((this.map as any).getLayer('geojson-layer-point')) {
        (this.map as any).removeLayer('geojson-layer-point');
      }
      if ((this.map as any).getSource('geojson-source')) {
        (this.map as any).removeSource('geojson-source');
      }
    }

    // Configurazione della mappa
    this.map = new maplibregl.Map({
      container: this.mapContainer.nativeElement,
      style: {
        version: 8,
        sources: {
          'osm-tiles': {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256
          }
        },
        layers: [
          {
            id: 'osm-tiles',
            type: 'raster',
            source: 'osm-tiles',
            minzoom: 0,
            maxzoom: 19
          }
        ]
      },
      center: [7.200, 45.1381], // Valle di Susa
      zoom: 10.5
    });

    // Aggiungi controlli alla mappa
    (this.map as any).addControl(new maplibregl.FullscreenControl(), 'top-right');

    // Quando la mappa è caricata
    this.map.on('load', () => {
      if (geoJSONData) {
        (this.map as any).addSource('geojson-source', {
          type: 'geojson',
          data: geoJSONData
        });

        // Aggiungiamo il layer di riempimento solo per poligoni
        (this.map as any).addLayer({
          id: 'geojson-layer-fill',
          type: 'fill',
          source: 'geojson-source',
          filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
          paint: {
            'fill-color': '#628395',
            'fill-opacity': 0.4,
            'fill-outline-color': '#628395'
          }
        });

        // Aggiungiamo il layer del contorno per linee e poligoni
        (this.map as any).addLayer({
          id: 'geojson-layer-line',
          type: 'line',
          source: 'geojson-source',
          filter: ['any',
            ['==', ['geometry-type'], 'LineString'],
            ['==', ['geometry-type'], 'MultiLineString'],
            ['==', ['geometry-type'], 'Polygon'],
            ['==', ['geometry-type'], 'MultiPolygon']
          ],
          paint: {
            'line-color': '#628395',
            'line-width': 2
          }
        });

        // Aggiungiamo il layer per i punti
        (this.map as any).addLayer({
          id: 'geojson-layer-point',
          type: 'circle',
          source: 'geojson-source',
          filter: ['any',
            ['==', ['geometry-type'], 'Point'],
            ['==', ['geometry-type'], 'MultiPoint']
          ],
          paint: {
            'circle-radius': 8,
            'circle-color': '#628395',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff'
          }
        });

        // Zoom sulla geometria caricata
        const bounds = new maplibregl.LngLatBounds();

        // Funzione per aggiungere coordinate ai bounds
        const addCoordinatesToBounds = (coordinates: any) => {
          if (Array.isArray(coordinates[0])) {
            coordinates.forEach((coord: number[] | number[][]) => addCoordinatesToBounds(coord));
          } else {
            // Assicuriamoci che le coordinate siano nel formato corretto (EPSG:4326)
            // Le coordinate dovrebbero essere già convertite nel GeoJSON, ma facciamo un controllo
            const [lon, lat] = coordinates;
            if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
              bounds.extend(coordinates as [number, number]);
            } else {
              console.warn('Coordinate non valide ignorate:', coordinates);
            }
          }
        };

        // Processa tutte le feature nel GeoJSON
        geoJSONData.features.forEach((feature: any) => {
          if (feature.geometry) {
            console.log('feature.geometry.type', feature.geometry.type);
            switch (feature.geometry.type) {
              case 'Point':
                const [pointLon, pointLat] = feature.geometry.coordinates;
                if (pointLon >= -180 && pointLon <= 180 && pointLat >= -90 && pointLat <= 90) {
                  bounds.extend(feature.geometry.coordinates as [number, number]);
                } else {
                  console.warn('Coordinate Point non valide ignorate:', feature.geometry.coordinates);
                }
                break;
              case 'MultiPoint':
                feature.geometry.coordinates.forEach((coord: number[]) => {
                  const [lon, lat] = coord;
                  if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
                    bounds.extend(coord as [number, number]);
                  } else {
                    console.warn('Coordinate MultiPoint non valide ignorate:', coord);
                  }
                });
                break;
              case 'LineString':
                feature.geometry.coordinates.forEach((coord: number[]) => {
                  const [lon, lat] = coord;
                  if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
                    bounds.extend(coord as [number, number]);
                  } else {
                    console.warn('Coordinate LineString non valide ignorate:', coord);
                  }
                });
                break;
              case 'MultiLineString':
                feature.geometry.coordinates.forEach((lineString: number[][]) => {
                  lineString.forEach((coord: number[]) => {
                    const [lon, lat] = coord;
                    if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
                      bounds.extend(coord as [number, number]);
                    } else {
                      console.warn('Coordinate MultiLineString non valide ignorate:', coord);
                    }
                  });
                });
                break;
              case 'Polygon':
                feature.geometry.coordinates[0].forEach((coord: number[]) => {
                  const [lon, lat] = coord;
                  if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
                    bounds.extend(coord as [number, number]);
                  } else {
                    console.warn('Coordinate Polygon non valide ignorate:', coord);
                  }
                });
                break;
              case 'MultiPolygon':
                feature.geometry.coordinates.forEach((polygon: any) => {
                  polygon[0].forEach((coord: number[]) => {
                    const [lon, lat] = coord;
                    if (lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90) {
                      bounds.extend(coord as [number, number]);
                    } else {
                      console.warn('Coordinate MultiPolygon non valide ignorate:', coord);
                    }
                  });
                });
                break;
              case 'GeometryCollection':
                feature.geometry.geometries.forEach((geom: any) => {
                  if (geom.coordinates) {
                    addCoordinatesToBounds(geom.coordinates);
                  }
                });
                break;
            }
          }
        });

        // Applica lo zoom con padding e limite di zoom
        if (!bounds.isEmpty()) {
          (this.map as any).fitBounds(bounds, {
            padding: { top: 50, bottom: 50, left: 50, right: 50 },
            maxZoom: 15,
            duration: 1000
          });
        } else {
          console.warn('Nessuna coordinata valida trovata per il calcolo dei bounds');
          // Imposta una vista predefinita
          (this.map as any).setCenter([7.200, 45.1381]); // Valle di Susa
          (this.map as any).setZoom(10.5);
        }
      }
    });
  }

  onGeoJSONClear(): void {
    this.uploadedGeoJSON = null;
    if (this.map) {
      if ((this.map as any).getLayer('geojson-layer-fill')) {
        (this.map as any).removeLayer('geojson-layer-fill');
      }
      if ((this.map as any).getLayer('geojson-layer-line')) {
        (this.map as any).removeLayer('geojson-layer-line');
      }
      if ((this.map as any).getLayer('geojson-layer-point')) {
        (this.map as any).removeLayer('geojson-layer-point');
      }
      if ((this.map as any).getSource('geojson-source')) {
        (this.map as any).removeSource('geojson-source');
      }
    }
  }

  salvaGeoJSON(): void {
    if (!this.scheda || !this.uploadedGeoJSON) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Nessun file GeoJSON caricato',
        life: 5000
      });
      return;
    }

    this.submitting = true;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const geoJSONContent = JSON.parse(e.target.result);
        let schedaDaAggiornare: SchedaAggiornaGeometriaDTO = {

          geoJSON: geoJSONContent
        };
        this.schedeService.modificaGeometria(this.scheda!.id, schedaDaAggiornare).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Successo',
              detail: 'GeoJSON caricato con successo',
              life: 5000
            });
            this.geojsonCaricato.emit(geoJSONContent);
            this.visible = false;
            this.reset();
          },
          error: (error: Error) => {
            console.error('Errore nel caricamento del GeoJSON:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Si è verificato un errore durante il caricamento del GeoJSON',
              life: 5000
            });
            this.submitting = false;
          }
        });
      } catch (error) {
        console.error('Errore nel parsing del GeoJSON:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nel processare il file GeoJSON',
          life: 5000
        });
        this.submitting = false;
      }
    };
    reader.readAsText(this.uploadedGeoJSON);
  }

  private reset(): void {
    this.uploadedGeoJSON = null;
    this.submitting = false;
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  onHide(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.reset();
  }
}
